/**
 * Master-data schemas shared by the API (route validation) and the web (form validation), so both
 * sides enforce identical rules (phone/GST/PAN formats, code lengths, date ranges).
 */
import { z } from "zod";
import { INDIAN_STATES } from "../gate-pass-in";
import { isIndia, POSTAL_CODE_PATTERN } from "../countries";
import { MOBILE_PATTERN, mobileNumber, normalizeMobile } from "../mobile";

export * from "./keys";
export * from "./locations";

// Indian-context validation (ported from vnr-prod helpers/regex.py). Phone: the shared mobile rule.
export const phone = mobileNumber;
export const gst = z
  .string()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, "invalid GSTIN");
export const pan = z.string().regex(/^[A-Z]{5}\d{4}[A-Z]$/, "invalid PAN");
export const pincode = z.string().regex(/^\d{6}$/, "6-digit PIN");

const ts = { createdAt: z.date(), updatedAt: z.date() };
const active = { isActive: z.boolean().default(true) };
const name = z.string().trim().min(1).max(100);
const code = z.string().trim().min(1).max(30);

export const facility = {
  create: z.object({
    plantCode: z.string().trim().min(1).max(3).toUpperCase(),
    name,
    type: z.enum(["internal", "external"]).default("internal"),
    // A third-party dryer on the cob chain. It still carries a plant code: the code ends every
    // document number minted there, which is what keeps those numbers from colliding with a plant's.
    isDryingFacility: z.boolean().default(false),
    // Hard stop on bin capacity (ledger.ts): a posting that overfills a bin is refused.
    enforceBinCapacity: z.boolean().default(false),
    gatePassInMode: z.enum(["system"]).default("system"),
    gatePassOutMode: z.enum(["system"]).default("system"),
    scaptureMappingCode: z.string().max(50).nullish(),
    iecCode: z.string().max(100).nullish(),
    address: z.string().max(300).nullish(),
    // The printed header's "Plant:" line, verbatim.
    printAddress: z.string().trim().max(300).nullish(),
    // The full name on forms and notes; `name` is the short one in the plant switcher.
    fullName: z.string().trim().max(200).nullish(),
    city: z.string().max(100).nullish(),
    state: z.string().max(100).nullish(),
    pincode: pincode.nullish(),
    country: z.string().max(60).nullish(),
    // Printed on the plant's dispatch notes (Orders and Dispatch, user 2026-10-30).
    gstin: gst.nullish(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    plantCode: z.string(),
    name: z.string(),
    type: z.string(),
    isDryingFacility: z.boolean(),
    enforceBinCapacity: z.boolean(),
    gatePassInMode: z.string(),
    gatePassOutMode: z.string(),
    scaptureMappingCode: z.string().nullable(),
    iecCode: z.string().nullable(),
    address: z.string().nullable(),
    printAddress: z.string().nullable(),
    fullName: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    pincode: z.string().nullable(),
    country: z.string().nullable(),
    gstin: z.string().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/** A plant's or drying facility's photo, sent as base64 — the desks have no multipart upload. */
export const FACILITY_PHOTO_MAX_BYTES = 3 * 1024 * 1024;
export const FACILITY_PHOTO_LIMIT = 10;
export const facilityPhoto = {
  create: z.object({
    fileName: z.string().trim().min(1).max(200),
    mimeType: z.enum(["image/jpeg", "image/png"]),
    base64: z
      .string()
      .min(1)
      .max(Math.ceil(FACILITY_PHOTO_MAX_BYTES / 3) * 4),
  }),
  response: z.object({
    id: z.number(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
    url: z.string(),
    createdAt: z.date(),
  }),
};
export const cropCategory = {
  create: z.object({ code, name, ...active }),
  response: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    coreId: z.number().nullable(),
    source: z.string(),
    coreDeletedAt: z.date().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const crop = {
  create: z.object({
    cropCategoryId: z.number().int(),
    code: code.nullish(),
    name,
    // Moisture above this fails QC; null leaves Pass/Fail to the supervisor. Local, never from Core.
    maxMoisturePct: z.number().positive().max(100).nullish(),
    // Stricter moisture limit for vapour-proof packing, checked at packing (at or under the normal
    // limit, which it needs; the database enforces it). Local.
    maxMoistureVpPct: z.number().positive().max(100).nullish(),
    // Physical purity standards (G9): a PP reading outside any of these is Not Acceptable. Local.
    minPurePct: z.number().positive().max(100).nullish(),
    maxInertPct: z.number().min(0).max(100).nullish(),
    maxOcsPerKg: z.number().int().min(0).nullish(),
    maxOdvPerKg: z.number().int().min(0).nullish(),
    // Germination rules, from the QC lab's crop sheet: seeds in one SGT / FGT replication, Size of
    // Seeds, and the minimum germination % to pass. Local, never from Core.
    germSeedsPerRepSgt: z.number().int().min(1).max(400).nullish(),
    germSeedsPerRepFgt: z.number().int().min(1).max(400).nullish(),
    germSeedSize: z.enum(["big", "small"]).nullish(),
    minGerminationPct: z.number().positive().max(100).nullish(),
    // The germination Fail mark: below it fails, between it and the OK mark (`minGerminationPct`)
    // is Borderline for the QC supervisor (docs/designs/qc-crop-standards.md). Local.
    germFailBelowPct: z.number().positive().max(100).nullish(),
    // Days after setup the lab takes the First and the Final Count (first before final; the database
    // enforces it), so the web can say when the germination result is due. Local.
    germFirstCountDay: z.number().int().min(1).max(60).nullish(),
    germFinalCountDay: z.number().int().min(1).max(60).nullish(),
    // Days a passed QC result keeps a lot cleared; null = never expires. Local, never from Core.
    qcValidityDays: z.number().int().min(1).max(3650).nullish(),
    gotStandardPpn: z.number().int().min(1).max(100000).nullish(),
    // GOT days from the Date of Sowing to the observation (Expected Date of Observation). Local.
    gotObservationDays: z.number().int().min(1).max(365).nullish(),
    // The crop's product number on the pack-line scale boxes (handheld Push label data). Local.
    scaleProductNo: z.number().int().min(1).max(999999).nullish(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    cropCategoryId: z.number().int(),
    code: z.string().nullable(),
    name: z.string(),
    maxMoisturePct: z.string().nullable(),
    maxMoistureVpPct: z.string().nullable(),
    minPurePct: z.string().nullable(),
    maxInertPct: z.string().nullable(),
    maxOcsPerKg: z.number().nullable(),
    maxOdvPerKg: z.number().nullable(),
    germSeedsPerRepSgt: z.number().nullable(),
    germSeedsPerRepFgt: z.number().nullable(),
    germSeedSize: z.string().nullable(),
    minGerminationPct: z.string().nullable(),
    germFailBelowPct: z.string().nullable(),
    germFirstCountDay: z.number().nullable(),
    germFinalCountDay: z.number().nullable(),
    qcValidityDays: z.number().nullable(),
    gotStandardPpn: z.number().nullable(),
    gotObservationDays: z.number().nullable(),
    scaleProductNo: z.number().nullable(),
    coreCropId: z.number().nullable(),
    source: z.string(),
    coreDeletedAt: z.date().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const classification = {
  create: z.object({
    name,
    classificationType: z.string().trim().max(10).nullish(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    name: z.string(),
    classificationType: z.string().nullable(),
    coreProductCategoryId: z.number().nullable(),
    source: z.string(),
    coreDeletedAt: z.date().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const variety = {
  create: z.object({
    cropId: z.number().int(),
    classificationId: z.number().int().positive().nullish(),
    name,
    code: z.string().trim().max(30).nullish(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    cropId: z.number().int(),
    classificationId: z.number().int().positive().nullable(),
    // Joined from classifications: Core ships two varieties per crop that differ only by it, so a
    // picker showing the bare name cannot be used.
    classificationName: z.string().nullable(),
    name: z.string(),
    code: z.string().nullable(),
    coreVarietyId: z.number().nullable(),
    source: z.string(),
    coreDeletedAt: z.date().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const stage = {
  create: z.object({
    code: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{0,4}$/, "letters only, up to 4")
      .transform((s) => s.toUpperCase()),
    name,
    description: z.string().max(500).nullish(),
    isDefault: z.boolean().default(false),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const lossType = {
  create: z.object({
    code: z.string().trim().min(1).max(3).toUpperCase(),
    name: z.string().trim().min(1).max(50),
    isLoss: z.boolean(),
    isAutoCalculated: z.boolean().default(false),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    isLoss: z.boolean(),
    isAutoCalculated: z.boolean(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/**
 * A drying machine's trays (bins), by name, each with the kg it holds when the plant keeps it
 * (A 20 kg, B 20 kg; 0150, kept on the Resource Master row since 0161). A floor has none.
 */
const dryingUnitTray = z.object({
  name: z
    .string({ message: "Name the tray" })
    .trim()
    .min(1, "Name the tray")
    .max(20, "20 characters at most"),
  capacityKg: z.coerce
    .number({ message: "Type the kg" })
    .positive("Must be more than 0")
    .max(100000)
    .nullish(),
});
export type DryingUnitTray = { name: string; capacityKg: number | null };
/** The first tray name typed twice on one machine (names compare case-blind), else null. */
export function duplicateTrayName(
  trays: readonly { name: string }[]
): string | null {
  const seen = new Set<string>();
  for (const t of trays) {
    const k = t.name.trim().toUpperCase();
    if (seen.has(k)) return t.name.trim();
    seen.add(k);
  }
  return null;
}
/** Resource Master kinds (seedtrac Admin, manual p37-43); see `processing_resources` in the API. */
export const PROCESSING_RESOURCE_KINDS = [
  "machine",
  "treatment",
  // The Drying Slip's Drying Details ID and the unit a Thermal batch goes in (user, 2026-10-27: back
  // from the retired Drying Unit master). A machine lists its trays; a floor has none.
  "dryer_floor",
  "dryer_machine",
  "pack_size",
  // A packing line and its inkjet coder (seedtrac Domino Machine Code Master, manual p41). The code
  // is one letter, A-J, and is the 2nd character of every pouch label number.
  "domino_line",
] as const;
export type ProcessingResourceKind = (typeof PROCESSING_RESOURCE_KINDS)[number];
export const PROCESSING_RESOURCE_LABELS: Record<
  ProcessingResourceKind,
  string
> = {
  machine: "Processing Machine Code",
  treatment: "Processing Treatment Type",
  dryer_floor: "Drying Floor",
  dryer_machine: "Drying Machine",
  pack_size: "Pack Size (UPS)",
  domino_line: "Domino Line (Pack Line)",
};
/** How a domino line packs: Manual (labels typed, no line link) or Online (line printer + scale). */
export const DOMINO_LINE_TYPES = ["manual", "online"] as const;
export type DominoLineType = (typeof DOMINO_LINE_TYPES)[number];
const processingResourceBase = z.object({
  kind: z.enum(PROCESSING_RESOURCE_KINDS),
  facilityId: z.number().int().positive().nullish(),
  code,
  name: z.string().trim().max(100).nullish(),
  colour: z.string().trim().max(40).nullish(),
  grams: z.coerce.number().positive().max(100000).nullish(),
  /** A count pack size (root stock): seeds in one packet; its grams are then the standard weight. */
  nos: z.coerce.number().int().positive().max(10_000_000).nullish(),
  lineType: z.enum(DOMINO_LINE_TYPES).nullish(),
  trays: z.array(dryingUnitTray).max(200, "200 trays at most").optional(),
  ...active,
});
export const processingResource = {
  base: processingResourceBase,
  create: processingResourceBase
    .refine((v) => v.kind !== "pack_size" || (v.grams ?? 0) > 0, {
      message: "A pack size needs its grams",
      path: ["grams"],
    })
    .refine((v) => v.kind === "pack_size" || v.nos == null, {
      message: "Only a pack size counts seeds",
      path: ["nos"],
    })
    .refine((v) => v.kind !== "domino_line" || /^[A-Z]$/.test(v.code), {
      message: "A domino line's code is one letter, A to Z",
      path: ["code"],
    })
    .refine((v) => v.kind !== "domino_line" || !!v.lineType, {
      message: "Pick Manual or Online",
      path: ["lineType"],
    })
    // The plant code leads every pouch label, so a line belongs to one plant.
    .refine((v) => v.kind !== "domino_line" || !!v.facilityId, {
      message: "A domino line belongs to one plant",
      path: ["facilityId"],
    })
    // The Drying Slip and Thermal list one plant's units.
    .refine(
      (v) =>
        (v.kind !== "dryer_floor" && v.kind !== "dryer_machine") ||
        !!v.facilityId,
      { message: "A drying unit belongs to one plant", path: ["facilityId"] }
    )
    .refine((v) => v.kind !== "dryer_machine" || (v.trays?.length ?? 0) >= 1, {
      message: "Add the machine's trays",
      path: ["trays"],
    })
    .refine((v) => !duplicateTrayName(v.trays ?? []), {
      message: "Each tray needs its own name",
      path: ["trays"],
    }),
  response: z.object({
    id: z.number(),
    kind: z.enum(PROCESSING_RESOURCE_KINDS),
    facilityId: z.number().nullable(),
    code: z.string(),
    name: z.string().nullable(),
    colour: z.string().nullable(),
    grams: z.string().nullable(),
    nos: z.number().nullish(),
    lineType: z.enum(DOMINO_LINE_TYPES).nullish(),
    trays: z.array(
      z.object({ name: z.string(), capacityKg: z.number().nullable() })
    ),
    isActive: z.boolean(),
    ...ts,
  }),
};
/**
 * How a crop's pouches of one size are boxed (docs/designs/packaging-module.md, P1): pouches per
 * window box (WB), WB weight, WBs per master pack (MP), MP weight. ST sizes fill whole WBs on a slip;
 * NST sizes take a typed MP weight there.
 */
export const UPS_TYPES = ["ST", "NST"] as const;
const packCount = z.coerce
  .number({ message: "Type a whole number" })
  .int("Type a whole number")
  .positive("Must be more than 0");
const packKg = z.coerce
  .number({ message: "Type the weight in kg" })
  .positive("Must be more than 0")
  .max(1000);
const packLayoutBase = z.object({
  cropId: z.number({ message: "Pick the crop" }).int().positive(),
  /** Set: this variety's own layout, overriding the crop's row of the same size. */
  varietyId: z.number().int().positive().nullish(),
  packSizeId: z.number({ message: "Pick the pack size" }).int().positive(),
  upstype: z.enum(UPS_TYPES, { message: "Pick ST or NST" }),
  pouchesInWb: packCount,
  wbWeightKg: packKg,
  wbInMp: packCount,
  mpWeightKg: packKg,
  maxMp: packCount.nullish(),
  ...active,
});
export const packLayout = {
  base: packLayoutBase,
  create: packLayoutBase.refine(
    (v) => v.mpWeightKg >= v.wbWeightKg * v.wbInMp - 1e-9,
    {
      message: "An MP weighs at least its window boxes (WB weight × WBs in MP)",
      path: ["mpWeightKg"],
    }
  ),
  response: z.object({
    id: z.number(),
    cropId: z.number(),
    varietyId: z.number().nullable(),
    packSizeId: z.number(),
    upstype: z.enum(UPS_TYPES),
    pouchesInWb: z.number(),
    wbWeightKg: z.string(),
    wbInMp: z.number(),
    mpWeightKg: z.string(),
    maxMp: z.number().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/**
 * MP Types (docs/designs/packaging-module.md D22): what an NST master pack is packed in, a carton or
 * a bag, and what the empty pack weighs. The NST Packing Slip types only the MP's GROSS weight and
 * takes this tare off it: pouches per MP = floor((gross - tare) / UPS), in whole grams. Per plant.
 */
export const MP_TYPE_KINDS = ["carton", "bag"] as const;
const mpTypeBase = z.object({
  code: z
    .string()
    .trim()
    .min(1, "Code is required")
    .max(20)
    .transform((s) => s.toUpperCase()),
  name: z.string().trim().min(1, "Name is required").max(80),
  mpType: z.enum(MP_TYPE_KINDS, { message: "Pick Carton or Bag" }),
  tareKg: z.coerce
    .number({ message: "Tare is required" })
    .min(0, "Tare cannot be below 0")
    .max(1000)
    .refine(
      (v) => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6,
      "Tare in kg with up to 3 decimals"
    ),
  facilityId: z.number({ message: "Pick the plant" }).int().positive(),
  ...active,
});
export const mpType = {
  create: mpTypeBase,
  response: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    mpType: z.enum(MP_TYPE_KINDS),
    tareKg: z.string(),
    facilityId: z.number(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/** A domino line's pouch labels per year: GET /masters/processing-resources/:id/next-labels. */
export const nextLabelsResponse = z.object({
  items: z.array(
    z.object({
      yearCode: z.string(),
      year: z.string(),
      last: z.string().nullable(),
      next: z.string(),
    })
  ),
});
/**
 * The seed-production staff an arrival is e-mailed to. `states` is free text, comma-separated as the
 * client's sheet writes it ("Andhra Pradesh, Telangana" or "ALL"): it orders the picker, and a head
 * (`isHead`) is copied on every arrival e-mail from a State it covers.
 */
export const productionPersonnel = {
  create: z.object({
    name,
    states: z.string().trim().max(300).nullish(),
    email: z.email().trim().toLowerCase().max(200),
    plantCode: z.string().trim().max(10).nullish(),
    // No .default(): the update schema is .partial(), and a default there would clear the flag on
    // every edit that leaves it out (a Restore). The column defaults to false.
    isHead: z.boolean().optional(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    name: z.string(),
    states: z.string().nullable(),
    email: z.string(),
    plantCode: z.string().nullable(),
    isHead: z.boolean(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/**
 * A QA Team member's type (user, 2026-10-24), for the dropdowns that will list only some of them.
 * The rows that were there before the type came start as Junior QA Analyst (0151).
 */
export const QA_TEAM_TYPES = [
  "qa_manager",
  "lab_supervisor",
  "qa_supervisor",
  "qa_analyst",
  "junior_qa_analyst",
] as const;
export type QaTeamType = (typeof QA_TEAM_TYPES)[number];
export const QA_TEAM_TYPE_LABELS: Record<QaTeamType, string> = {
  qa_manager: "QA Manager",
  lab_supervisor: "Lab Supervisor",
  qa_supervisor: "QA Supervisor",
  qa_analyst: "QA Analyst",
  junior_qa_analyst: "Junior QA Analyst",
};
/**
 * The floor's types (user, 2026-10-25): the ones listed as Sampled by on Sample Generation and as
 * Tested by on a result. Lab Supervisors and QA Managers stay off both lists.
 */
export const QA_TEAM_FLOOR_TYPES = [
  "junior_qa_analyst",
  "qa_analyst",
  "qa_supervisor",
] as const satisfies readonly QaTeamType[];
/**
 * QA Team, was QC Analysts (user, 2026-10-23; renamed 2026-10-24): the lab's named staff per plant,
 * each one's type and the tests each does. A test is allocated to a login user; the result records
 * which of these did it. The key, path and table keep the old name, which handhelds in use call.
 */
export const qcAnalysts = {
  create: z.object({
    facilityId: z.number({ message: "Pick the plant" }).int().positive(),
    name,
    memberType: z.enum(QA_TEAM_TYPES, { message: "Pick the type" }),
    phone: z.string().trim().max(20).nullish(),
    // No .default(): the update schema is .partial(), and a default there would clear the flag on
    // every edit that leaves it out. The columns default to false.
    doesMoisture: z.boolean().optional(),
    doesPp: z.boolean().optional(),
    doesGermination: z.boolean().optional(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    facilityId: z.number(),
    name: z.string(),
    memberType: z.enum(QA_TEAM_TYPES),
    phone: z.string().nullable(),
    doesMoisture: z.boolean(),
    doesPp: z.boolean(),
    doesGermination: z.boolean(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/**
 * Operator Master (user, 2026-10-27; 0162): the plant's machine operators, out of Resource Master,
 * each on one team. A slip's Operator Name lists only the teams its form allows
 * (`OPERATOR_TEAMS_BY_FORM`); Drying, Thermal and Priming are run by the Processing team and name
 * no operator.
 */
export const OPERATOR_TEAMS = [
  "processing",
  "packing",
  // The sections a Priming / Thermal lot is issued to (user, 2026-10-29); their slip's Supervisor.
  "rnd",
  "thermal",
] as const;
export type OperatorTeam = (typeof OPERATOR_TEAMS)[number];
export const OPERATOR_TEAM_LABELS: Record<OperatorTeam, string> = {
  processing: "Processing",
  packing: "Packing",
  rnd: "R&D",
  thermal: "Thermal",
};
/** The teams whose operators a form's Operator Name offers; a form not listed takes any team. */
export const OPERATOR_TEAMS_BY_FORM: Record<string, readonly OperatorTeam[]> = {
  processing_slip: ["processing"],
  packing_work_order: ["packing"],
  reprint_work_order: ["packing"],
  ofl_packing_slip: ["packing"],
  fc_packing_slip: ["packing"],
  processing_packing_slip: ["processing", "packing"],
  // The Color Sorting Slip's Supervisor (user, 2026-10-28).
  processing_color_sort: ["processing"],
  // Priming and Thermal are worked by the section they are issued to (user, 2026-10-29).
  processing_priming: ["rnd"],
  processing_thermal: ["thermal"],
};
export const operator = {
  create: z.object({
    facilityId: z.number({ message: "Pick the plant" }).int().positive(),
    code,
    name,
    team: z.enum(OPERATOR_TEAMS, { message: "Pick the team" }),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    facilityId: z.number(),
    code: z.string(),
    name: z.string(),
    team: z.enum(OPERATOR_TEAMS),
    isActive: z.boolean(),
    ...ts,
  }),
};
/** Unloading Sheet CC: copied on every arrival's Unloading Sheet e-mail while Active. */
export const emailCc = {
  create: z.object({
    name,
    email: z.email().trim().toLowerCase().max(200),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/** Whether someone covering `states` covers `state`: "ALL", or the state named in the list. */
export function coversState(
  states: string | null | undefined,
  state: string | null | undefined
): boolean {
  const list = (states ?? "")
    .split(",")
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
  if (list.includes("all")) return true;
  const want = (state ?? "").trim().toLowerCase();
  return want !== "" && list.includes(want);
}
const yearCodeBase = z.object({
  code: z.string().trim().min(1).max(10).toUpperCase(),
  fromDate: z.iso.date(),
  toDate: z.iso.date(),
});
export const yearCode = {
  base: yearCodeBase,
  create: yearCodeBase.refine((v) => v.fromDate <= v.toDate, {
    message: "fromDate must not be after toDate",
    path: ["toDate"],
  }),
  response: z.object({
    id: z.number(),
    code: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    ...ts,
  }),
};
/**
 * An address in India or abroad. `pincode` holds the postcode either way: six digits in India,
 * a foreign postcode (Thailand 10110, South Africa 2196) anywhere else. The country decides which
 * rule applies, so the field is judged in `checkAddress`, not here.
 */
export const addressBase = z.object({
  label: z.string().max(60).nullish(),
  line1: z.string().trim().min(1).max(150),
  line2: z.string().max(150).nullish(),
  city: z.string().max(100).nullish(),
  state: z.string().max(100).nullish(),
  pincode: z.string().trim().max(10).nullish(),
  country: z.string().trim().max(60).default("India"),
  isDefault: z.boolean().default(false),
});
type AddressShape = {
  [K in keyof z.output<typeof addressBase>]?:
    z.output<typeof addressBase>[K] | undefined;
};

/**
 * The country-dependent rules. India: a 6-digit PIN, and the state from the fixed list (both
 * required when `requireIndianParts`, as they are on a new address). Abroad: a free-text region and
 * an optional postcode. A PATCH that sends neither country nor postcode is not judged here.
 */
function checkAddress(
  a: AddressShape,
  ctx: z.RefinementCtx,
  { requireIndianParts }: { requireIndianParts: boolean }
) {
  const pin = a.pincode?.trim();
  if (isIndia(a.country)) {
    if (pin ? !/^\d{6}$/.test(pin) : requireIndianParts)
      ctx.addIssue({
        code: "custom",
        path: ["pincode"],
        message: "6-digit PIN",
      });
    const state = a.state?.trim();
    if (
      state
        ? !(INDIAN_STATES as readonly string[]).includes(state)
        : requireIndianParts
    )
      ctx.addIssue({
        code: "custom",
        path: ["state"],
        message: "Pick the state",
      });
  } else if (pin && !POSTAL_CODE_PATTERN.test(pin)) {
    ctx.addIssue({
      code: "custom",
      path: ["pincode"],
      message: "Postal code: 2–10 letters or digits",
    });
  }
}
export const address = addressBase.superRefine((a, ctx) =>
  checkAddress(a, ctx, { requireIndianParts: true })
);
/** A partial update: judged only as far as the body goes (a label-only PATCH passes). */
export const addressPatch = addressBase
  // no "India" default here: a body that leaves the country out must not be judged as Indian
  .extend({ country: z.string().trim().max(60) })
  .partial()
  .superRefine((a, ctx) =>
    a.country !== undefined || a.pincode !== undefined || a.state !== undefined
      ? checkAddress(a, ctx, { requireIndianParts: a.country !== undefined })
      : undefined
  );
/**
 * One address on one line — `line1, line2, city, state, pincode, country`, blanks dropped.
 *
 * Trading Arrival prints this as "Vendor Address" on TRN-I/TRN-P, and the vendor master's address
 * card shows the same string; one implementation so the slip and the screen cannot disagree.
 * `plantAddressLine()` in `../dispatch-stock-transfer` is the same shape over a facility.
 */
export function addressLine(a: {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
}): string {
  return [a.line1, a.line2, a.city, a.state, a.pincode, a.country]
    .map((v) => (v ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

export const addressResponse = z.object({
  id: z.number(),
  label: z.string().nullable(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  pincode: z.string().nullable(),
  country: z.string().nullable(),
  isDefault: z.boolean(),
  ...ts,
});
/**
 * A vendor's phone: an Indian mobile (normalised to ten digits, as everywhere), or — for a vendor
 * abroad — `+` country code and number, kept as `+66812345678`.
 */
export const INTL_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;
export const VENDOR_PHONE_MESSAGE =
  "10-digit mobile, or + country code and number abroad";
export const vendorPhone = z
  .string()
  .transform((v) => {
    const compact = v.trim().replace(/[\s().-]/g, "");
    return /^\+(?!91)/.test(compact) ? compact : normalizeMobile(v);
  })
  .pipe(
    z
      .string()
      .refine(
        (v) => MOBILE_PATTERN.test(v) || INTL_PHONE_PATTERN.test(v),
        VENDOR_PHONE_MESSAGE
      )
  );
const vendorCreate = z.object({
  name,
  headName: z.string().max(100).nullish(),
  phone: vendorPhone.nullish(),
  secondaryPhone: vendorPhone.nullish(),
  email: z.email().nullish(),
  natureOfBusiness: z
    .enum(["manufacturer", "service_provider", "reseller", "trader"])
    .nullish(),
  vendorCode: z.string().max(100).nullish(),
  gstNumber: gst.nullish(),
  panNumber: pan.nullish(),
  suspendedUntil: z.iso.datetime().nullish(),
  /** Supplies the consumables store (stickers, rolls, bags...): listed on Stores ▸ Arrival from Vendor. */
  storesVendor: z.boolean().optional(),
  ...active,
});
export const vendor = {
  create: vendorCreate,
  /**
   * A hand-made vendor and its registered address in one body (the New vendor form). A GSTIN is an
   * Indian registration, so a vendor abroad cannot carry one.
   */
  createWithAddress: vendorCreate
    .extend({ address: address })
    .superRefine((v, ctx) => {
      if (!v.address.city?.trim())
        ctx.addIssue({
          code: "custom",
          path: ["address", "city"],
          message: "Required",
        });
      if (v.gstNumber && !isIndia(v.address.country))
        ctx.addIssue({
          code: "custom",
          path: ["gstNumber"],
          message: "GSTIN is for vendors in India",
        });
    }),
  response: z.object({
    id: z.number(),
    name: z.string(),
    headName: z.string().nullable(),
    phone: z.string().nullable(),
    secondaryPhone: z.string().nullable(),
    email: z.string().nullable(),
    natureOfBusiness: z.string().nullable(),
    vendorCode: z.string().nullable(),
    gstNumber: z.string().nullable(),
    panNumber: z.string().nullable(),
    source: z.string(),
    coreVendorId: z.number().nullable(),
    coreDeletedAt: z.date().nullable(),
    coreFarmer: z.string().nullable(),
    suspendedUntil: z.date().nullable(),
    storesVendor: z.boolean(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const spDecode = {
  create: z.object({
    spMale: z.string().trim().min(1).max(20).toUpperCase(),
    spFemale: z.string().trim().min(1).max(20).toUpperCase(),
    cropId: z.number().int(),
    varietyId: z.number().int(),
  }),
  response: z.object({
    id: z.number(),
    spMale: z.string(),
    spFemale: z.string(),
    cropId: z.number().int(),
    varietyId: z.number().int(),
    decodedBy: z.number().nullable(),
    decodedAt: z.date(),
  }),
};
export const storageRestriction = {
  create: z
    .object({
      varietyAId: z.number().int().positive(),
      varietyBId: z.number().int().positive(),
    })
    .refine((v) => v.varietyAId !== v.varietyBId, {
      message: "a variety cannot be restricted against itself",
      path: ["varietyBId"],
    }),
  response: z.object({
    varietyAId: z.number(),
    varietyBId: z.number(),
    createdBy: z.number().nullable(),
    createdAt: z.date(),
  }),
};

// ---- Stores (stores.ts) --------------------------------------------------------------------------

/** Stores UoM: Kg, Number, Litres... `decimals` is how finely its Qty is kept (Number 0, Kg 3). */
export const storesUom = {
  create: z.object({
    code: z.string().trim().min(1).max(10),
    name: z.string().trim().min(1).max(50),
    decimals: z.number().int().min(0).max(3).default(3),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    decimals: z.number().int(),
    isActive: z.boolean(),
    ...ts,
  }),
};
export const storesClassification = {
  create: z.object({ name, ...active }),
  response: z.object({
    id: z.number(),
    name: z.string(),
    isActive: z.boolean(),
    ...ts,
  }),
};
const reorderLevel = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .pipe(
    z.string().regex(/^\d{1,11}(\.\d{1,3})?$/, "a quantity, up to 3 decimals")
  );
const storesItemBase = z.object({
  classificationId: z.number().int().positive(),
  name: z.string().trim().min(1).max(150),
  uomId: z.number().int().positive(),
  /** seedtrac's "Select Re-Order Level: Yes / No", then the level itself. */
  reorderEnabled: z.boolean().default(false),
  reorderLevel: reorderLevel.nullish(),
  ...active,
});
export const storesItem = {
  base: storesItemBase,
  create: storesItemBase.superRefine((v, ctx) => {
    if (v.reorderEnabled && !v.reorderLevel)
      ctx.addIssue({
        code: "custom",
        path: ["reorderLevel"],
        message: "Required",
      });
  }),
  response: z.object({
    id: z.number(),
    classificationId: z.number(),
    classificationName: z.string().nullable(),
    name: z.string(),
    uomId: z.number(),
    reorderEnabled: z.boolean(),
    reorderLevel: z.string().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};
/**
 * A Stores party that is neither a vendor nor one of our plants: C&F agents, dealers, and (since
 * 2026-10-13) seedtrac's Stock Transfer parties and Own units that are not NexSeed plants. Those two
 * are named as seedtrac lists them, with no address; the others need theirs.
 */
const STORES_PARTY_CATEGORY_VALUES = [
  "cnf",
  "dealer",
  "other",
  "stock_transfer",
  "own_unit",
] as const;
const storesPartyBase = z.object({
  category: z.enum(STORES_PARTY_CATEGORY_VALUES),
  name: z.string().trim().min(1).max(150),
  contactPerson: z.string().trim().max(100).nullish(),
  address: z.string().trim().max(500).nullish(),
  city: z.string().trim().max(100).nullish(),
  pincode: pincode.nullish(),
  state: z.string().trim().max(100).nullish(),
  country: z.string().trim().max(100).default("India"),
  phone: z.string().trim().max(30).nullish(),
  mobile: phone.nullish(),
  gstNumber: gst.nullish(),
  panNumber: pan.nullish(),
  ...active,
});
export const storesParty = {
  base: storesPartyBase,
  create: storesPartyBase.superRefine((v, ctx) => {
    if (v.category === "stock_transfer" || v.category === "own_unit") return;
    for (const k of ["address", "city", "state"] as const)
      if (!v[k]?.trim())
        ctx.addIssue({ code: "custom", path: [k], message: "Required" });
  }),
  response: z.object({
    id: z.number(),
    category: z.string(),
    name: z.string(),
    contactPerson: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    pincode: z.string().nullable(),
    state: z.string().nullable(),
    country: z.string(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    gstNumber: z.string().nullable(),
    panNumber: z.string().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};

/**
 * Customer Master (Orders and Dispatch, user 2026-10-30): the parties orders are booked for and
 * dispatches go to -- VNR's branches and C&F depots, dealers, bulk and export buyers, TDF individuals.
 * Loaded from SeedTrac's order and dispatch notes (scripts/load-customers-seedtrac.ts); `legacyName`
 * keeps SeedTrac's "Name-Location" so its history can be matched back. Phone numbers are kept as
 * SeedTrac wrote them (several per party, landlines with STD codes), so they are free text.
 */
export const CUSTOMER_TYPE_VALUES = [
  "branch",
  "cnf",
  "dealer",
  "bulk",
  "export_buyer",
  "tdf_individual",
  "discard_buyer",
] as const;
export const customer = {
  create: z.object({
    customerType: z.enum(CUSTOMER_TYPE_VALUES, { message: "Pick the type" }),
    name: z.string().trim().min(1).max(200),
    location: z.string().trim().max(100).nullish(),
    address: z.string().trim().max(600).nullish(),
    city: z.string().trim().max(100).nullish(),
    state: z.string().trim().max(100).nullish(),
    pincode: z.string().trim().max(12).nullish(),
    country: z.string().trim().max(100).default("India"),
    gstin: gst.nullish(),
    pan: pan.nullish(),
    phone: z.string().trim().max(120).nullish(),
    mobile: z.string().trim().max(120).nullish(),
    contactPerson: z.string().trim().max(100).nullish(),
    legacyName: z.string().trim().max(250).nullish(),
    focusCode: z.string().trim().max(20).nullish(),
    ...active,
  }),
  response: z.object({
    id: z.number(),
    customerType: z.string(),
    name: z.string(),
    location: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    pincode: z.string().nullable(),
    country: z.string(),
    gstin: z.string().nullable(),
    pan: z.string().nullable(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    contactPerson: z.string().nullable(),
    legacyName: z.string().nullable(),
    focusCode: z.string().nullable(),
    isActive: z.boolean(),
    ...ts,
  }),
};

/** A null variety applies to every variety of the selected crop. */
export const treatmentMapping = z.object({
  cropId: z.number().int().positive(),
  varietyId: z.number().int().positive().nullable(),
});
export const treatmentSchema = z.object({
  code,
  colour: z.string().trim().min(1).max(40),
  facilityId: z.number().int().positive().nullish(),
  isActive: z.boolean().default(true),
  mappings: z
    .array(treatmentMapping)
    .min(1, "Add at least one crop or variety")
    .max(200)
    .refine(
      (rows) =>
        new Set(rows.map((r) => `${r.cropId}:${r.varietyId}`)).size ===
        rows.length,
      "Remove duplicate crop/variety mappings"
    ),
});
